import { Controller } from "@hotwired/stimulus"
import { fieldGroup, isFieldShown } from "../../src/shared/helpers/form_field_state"

// Mirrors what the admin filled in elsewhere in the form, so the last step can
// recap it before saving. Each line lists the field ids it joins
// (`data-fields`, `data-separator`), or holds one chip per switch
// (`data-chips`, `data-check-id`), shown while it is on. Values containing
// one of `data-ignore` count as blank, like the server does (e.g. Doctolib
// URL placeholders).
// Fields hidden by the form (e.g. new-user fields when an existing account is
// picked) are skipped. An empty line shows the empty label, or hides when it
// is `data-optional`.
export default class extends Controller {
  static targets = ["line"]
  static classes = ["empty"]
  static values = { empty: String }

  connect () {
    this.form = this.element.closest("form")
    if (!this.form) return

    this.refresh = this.refresh.bind(this)
    this.events = ["input", "change", "click"]
    this.events.forEach((event) => this.form.addEventListener(event, this.refresh))
    this.refresh()
    // Tom Select and the user mode switch settle after this controller connects.
    this.refreshTimeout = setTimeout(this.refresh)
  }

  disconnect () {
    clearTimeout(this.refreshTimeout)
    this.events?.forEach((event) => this.form.removeEventListener(event, this.refresh))
  }

  refresh () {
    this.lineTargets.forEach((line) => {
      if (line.dataset.chips) {
        this.refreshChips(line)
        return
      }

      const text = this.lineText(line)

      line.textContent = text || this.emptyValue
      line.hidden = !text && line.dataset.optional === "true"
      if (this.hasEmptyClass) line.classList.toggle(this.emptyClass, !text)
    })
  }

  // `jump` lets the surrounding section menu show the field's section first.
  jump ({ params: { fieldId } }) {
    const group = fieldGroup(this.form, fieldId)
    if (!group) return

    this.dispatch("jump", { detail: { element: group } })

    const details = group.closest("details")
    if (details) details.open = true

    group.scrollIntoView({ behavior: "smooth", block: "center" })
    group.querySelector("input:not([type=hidden]), select, textarea")?.focus({ preventScroll: true })
  }

  refreshChips (line) {
    let any = false

    line.querySelectorAll("[data-check-id]").forEach((chip) => {
      const on = Boolean(document.getElementById(chip.dataset.checkId)?.checked)
      chip.hidden = !on
      if (on) any = true
    })

    const empty = line.querySelector("[data-recap-empty]")
    if (empty) empty.hidden = any
  }

  lineText (line) {
    const ignored = JSON.parse(line.dataset.ignore || "[]")

    return JSON.parse(line.dataset.fields || "[]")
      .map((fieldId) => this.fieldText(fieldId))
      .filter((text) => text && !ignored.some((part) => text.includes(part)))
      .join(line.dataset.separator || " ")
  }

  fieldText (fieldId) {
    const group = fieldGroup(this.form, fieldId)
    if (!group || !isFieldShown(group)) return ""

    const control = group.querySelector("input:not([type=hidden]), select, textarea")
    if (!control) return ""

    if (control.tagName === "SELECT") {
      return Array.from(control.selectedOptions)
        .filter((option) => option.value)
        .map((option) => option.textContent.trim())
        .join(", ")
    }
    if (control.type === "radio") {
      return group.querySelector("input[type=radio]:checked")?.closest("label")?.textContent.trim() || ""
    }
    if (control.type === "checkbox") return ""

    return control.value.trim()
  }
}
